import { ReactNode } from 'react';
import { Link, Stack, Typography } from '@mui/material';
import {
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  FileTextOutlined,
  GiftOutlined,
  HistoryOutlined,
  PauseCircleOutlined,
  PercentageOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { formatEndsOn } from 'sections/account-settings/subscriptions/checkout-v2/Configure/formatEndsOn';
import type { FreezeQuote } from 'features/subscription-freeze/types';
import { PAUSE_UNAVAILABLE_STEP, PauseBlockerFacts, SUPPORT_LINK } from '../content';
import { CONSEQUENCE_ICONS, Group, GroupList, IconRow } from './StepParts';

const BLOCKER_ICONS: Record<string, ReactNode> = {
  unsupported_ea_hosting: CONSEQUENCE_ICONS.eaHosting,
  trial_subscription: <GiftOutlined />,
  freeze_cycle_limit_reached: <HistoryOutlined />,
  freeze_resume_cooldown_active: <ClockCircleOutlined />,
  payment_method_required: <CreditCardOutlined />,
  billing_delinquent: <ExclamationCircleOutlined />,
  unresolved_subscription_billing: <SyncOutlined />,
  subscription_renewing: <CalendarOutlined />,
  unresolved_crypto_payment: <WalletOutlined />,
  invoice_billed_subscription: <FileTextOutlined />,
  unsupported_subscription_tax: <PercentageOutlined />,
  normal_subscription_required: <AppstoreOutlined />,
  subscriptions_ending: <FieldTimeOutlined />,
  freeze_already_open: <PauseCircleOutlined />
};

// Codes this screen has no words for share one line, however many of them arrive.
const UNKNOWN_BLOCKER = 'unknown';
const known = (code: string) => (PAUSE_UNAVAILABLE_STEP.reasons[code] ? code : UNKNOWN_BLOCKER);
const COOLDOWN_BLOCKER = 'freeze_resume_cooldown_active';

// The cooldown lifts on its own date; the yearly limit on the next one billing-manager worked out.
function blockerFacts(code: string, quote: FreezeQuote): PauseBlockerFacts {
  const { eligibility } = quote;
  const lifts = code === COOLDOWN_BLOCKER ? eligibility?.cooldownEndsAt : eligibility?.nextEligibleAt;
  return { date: formatEndsOn(lifts) || undefined, limit: eligibility?.freezeCyclesLimit };
}

/** Every reason billing-manager refused a pause, each with what would clear it, and a way to ask. */
export default function PauseUnavailable({ quote, onSupport }: { quote: FreezeQuote; onSupport: () => void }) {
  const codes = [...new Set(quote.ineligibleReasonCodesList.map(known))];
  return (
    <Stack spacing={2}>
      <GroupList>
        <Group label={PAUSE_UNAVAILABLE_STEP.group}>
          {codes.map((code) => {
            const copy = PAUSE_UNAVAILABLE_STEP.reasons[code] || PAUSE_UNAVAILABLE_STEP.unknown;
            return (
              <IconRow
                key={code}
                icon={BLOCKER_ICONS[code] || <QuestionCircleOutlined />}
                tone="warning"
                label={copy.title}
                detail={copy.detail(blockerFacts(code, quote))}
              />
            );
          })}
        </Group>
      </GroupList>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        {PAUSE_UNAVAILABLE_STEP.helpNote}{' '}
        <Link component="button" type="button" variant="body2" onClick={onSupport} sx={{ verticalAlign: 'baseline' }}>
          {SUPPORT_LINK}
        </Link>
      </Typography>
    </Stack>
  );
}
